import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ThemeToggle } from '../../components';
import { useAuth } from '../../context';
import { apiService } from '../../services';
import { getDefaultLandingPath } from '../../utils/defaultLanding';
import './Login.css';

const BrandMark = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
    <path d="M3 3v18h18" />
    <path d="M7 14l4-4 3 3 5-6" />
  </svg>
);

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const GoogleIcon = () => (
  <svg className="login__google-icon" viewBox="0 0 48 48" aria-hidden>
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.2 5.5-4.7 7.2l7.3 5.7c4.3-4 6.7-9.8 6.7-17.4z" />
    <path fill="#FBBC05" d="M10.5 28.7A14.5 14.5 0 019.5 24c0-1.6.3-3.2.8-4.7l-7.9-6.1A24 24 0 000 24c0 3.9.9 7.5 2.6 10.8l7.9-6.1z" />
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.3-5.7c-2 1.4-4.6 2.3-8.6 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.9 6.1C6.5 42.6 14.6 48 24 48z" />
  </svg>
);

export const Login: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const expired = searchParams.get('expired') === '1';
  const [error] = useState<string | null>(
    expired ? 'Your session expired. Please sign in again.' : null
  );

  useEffect(() => {
    if (isAuthenticated) {
      window.location.replace(getDefaultLandingPath());
    }
  }, [isAuthenticated]);

  if (isLoading || isAuthenticated) {
    return (
      <div className="login">
        <div className="login__card">
          <div className="login__loading">
            <div className="login__spinner" />
            <span className="login__loading-text">
              {isAuthenticated ? 'Taking you to your home…' : 'Checking authentication…'}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login">
      <div className="login__theme">
        <ThemeToggle labeled />
      </div>

      <main className="login__main">
        <div className="login__brand">
          <div className="login__mark">
            <BrandMark />
          </div>
          <p className="login__product">
            Prompt Analysis <span>|</span> YorkIE
          </p>
        </div>

        <div className="login__card">
          <div className="login__card-rule" aria-hidden />
          <div className="login__header">
            <h1 className="login__title">Sign in</h1>
            <p className="login__subtitle">Use your company Google account to continue.</p>
          </div>

          {error && (
            <div className="login__error" role="alert">
              <span className="login__error-icon"><AlertIcon /></span>
              <p className="login__error-text">{error}</p>
            </div>
          )}

          <button
            type="button"
            className="login__google-btn"
            onClick={() => window.location.assign(apiService.getGoogleAuthUrl())}
          >
            <GoogleIcon />
            Continue with Google
          </button>

          <p className="login__footer-text">
            Access is limited to <b>YorkIE</b> accounts.
          </p>
        </div>
      </main>
    </div>
  );
};

export default Login;
